import {
  FetchQueryOptions,
  MutationKey,
  QueryKey,
  useMutationState,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { queryClient } from "services";

import { QueryOptions } from "./types";

export function getQueryData<TQueryFnData>(
  queryOptions: QueryOptions<TQueryFnData | undefined>
) {
  return queryClient.getQueryData<TQueryFnData | undefined>(
    queryOptions.queryKey
  );
}

export function usePrefetchQuery<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(options: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const queryClient = useQueryClient();

  queryClient.ensureQueryData(options);
}

export function usePrefetchQueryOnCondition<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  options: FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  condition: boolean
) {
  if (!condition) {
    return;
  }

  queryClient.ensureQueryData(options);
}

export function useQuerySelector<
  TQueryFnData,
  TData = TQueryFnData,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey
>(
  queryOptions: UseQueryOptions<TQueryFnData, TError, TQueryFnData, TQueryKey>,
  select: (data: TQueryFnData) => TData
): UseQueryResult<TData, TError> {
  return useQuery({
    ...queryOptions,
    select
  });
}

export function useIsMutationPending(mutationKey: MutationKey) {
  const isPending = useMutationState({
    filters: { mutationKey, status: "pending" },
    select: mutation => mutation.state.status
  });
  return isPending.length ? isPending[0] === "pending" : false;
}
